// Item perks (krug 4, plan v0.6 section E). Every MVP item has a signature perk (Rare,
// stronger on Epic) and a Legendary perk; the data is item_perks_mvp.json (v2), checked
// with Zod when the module loads. Common and Uncommon have no perk. New Rare+ items carry
// `passive: { id: 'signature' }`; items from before krug 4 keep their old pool perk only.

import { z } from 'zod';
import raw from './item_perks_mvp.json';
import { MVP_ITEMS } from './mvpItems';
import { ZONES } from '../content/zones';
import type { ItemInstance, Rarity, Stance, ZoneId } from '../types';

/** The 101 effect types, by where they act (plan v0.6, E1; 14 came with krug 5, F10; the last 4 on 7. 10. 2026). */
export const PERK_EFFECT_GROUPS = {
  /** Folded into the derived stats (and the stat breakdown) or read where the stat is used. */
  stats: [
    'accuracyPct', 'critPct', 'dodgePct', 'escapePct', 'defensePct', 'damagePct', 'damageTakenPct', 'statFlat', 'capacityFlat', 'capacityNoPenaltyFlat',
    'initiativePct', 'lootPct', 'xpPct', 'healingPct', 'medkitEffectPct', 'healingItemPct', 'craftTimePct', 'repairCostPct', 'dismantleScrapPct', 'mealEffectPct', 'mealFoodPct',
    'encounterChancePct', 'detectedPct', 'stealSuccessPct', 'durationPct', 'cargoEscapePenaltyPct', 'consumableLimit', 'followTicks', 'ammoUsePct',
    'selfDurabilityLossPct', 'gearDurabilityLossPct', 'pvpCargoLossPct', 'extraResourceChance',
    'detectPct', 'discoveryPct', 'statPct', 'resourceFindPct', 'allStatsPctDuringRaid',
  ],
  /** Hooks inside `simulateCombat`. */
  combat: [
    'ambushDamagePct', 'ambushFirstHitDamagePct', 'critDefenseShredPct', 'critExecuteBelowHpPct', 'critHealPct', 'critVsStrongerPct', 'damagePerKillPct',
    'damageVsPct', 'defenseVsPct', 'enemyAccuracyFirstRoundPct', 'enemyDefensePct', 'enemyEscapePct', 'everyNthHitCrit', 'everyNthHitDamagePct',
    'extraAttackChance', 'extraHitChance', 'firstHitBlocked', 'firstHitStunChance', 'firstMeleeHitMisses', 'firstRangedHitBlocked', 'firstRoundDamagePct',
    'ignoreDefensePct', 'meleeReflectChance', 'npcFleeAtStartChance', 'round0CritPct', 'shadowAmbushAlwaysCrit', 'zoneDamagePct', 'burnOnHit',
    'armorBreakChance', 'meleeArmorBreak', 'autoMedkitInFightBelowHpPct', 'weaponNoDurabilityLoss', 'noAmmoVsNpc', 'noFuelUse',
    'firstCritAgainstNormal', 'neverAttackedFirst',
    // 7. 10. 2026 (owner): the Legendary perks that only saved a resource got a main effect in a fight.
    'firstShotsNeverMiss', 'everyNthHitStun', 'selfPatchOncePct', 'cheatDeathOnce',
  ],
  /** The expedition and what happens after a fight. */
  expedition: [
    'hpAfterFightPct', 'hpAfterWinPct', 'hpOnReturnPct', 'itemAfterFightChance', 'specialFindOnWinChance', 'hiddenStashChance', 'freeItemOnSend', 'dropRarityUpChance', 'craftRarityUpChance',
    'durationCutOnEscapePct', 'firstNpcEscapeAlwaysSucceeds', 'instantFinishPerDay', 'specialFindMultiplier',
  ],
  /** Encounters with other survivors and hiding. */
  encounter: [
    'revealEnemyStance', 'revealShadowOnEncounter', 'anonymous', 'cannotBeFollowed', 'encounterChanceCapAtBase', 'escapeKeepsCargo', 'failedStealNoFight', 'silent',
    'revealEnemyPowerAndCargo', 'revealFollowerAfterTicks', 'rumorReliability',
  ],
} as const;

export type PerkEffectType = (typeof PERK_EFFECT_GROUPS)[keyof typeof PERK_EFFECT_GROUPS][number];
export const PERK_EFFECT_TYPES = Object.values(PERK_EFFECT_GROUPS).flat() as PerkEffectType[];

/**
 * `armored` = WARDEN drones and survivors with 30+ armor; `horde` = a Horde event fight;
 * `boss` = the boss event (The Brute, The Warlord, The Hive) and its tide (krug 5, F10).
 */
export const PERK_TARGETS = ['infected', 'raider', 'mutant', 'drone', 'armored', 'horde', 'boss'] as const;
export type PerkTarget = (typeof PERK_TARGETS)[number];

const zEffect = z
  .object({
    type: z.enum(PERK_EFFECT_TYPES as [PerkEffectType, ...PerkEffectType[]]),
    value: z.union([z.number(), z.boolean()]),
    zones: z.array(z.string()).optional(),
    targets: z.array(z.enum(PERK_TARGETS)).optional(),
    attack: z.enum(['melee', 'ranged']).optional(),
    belowHpPct: z.number().optional(),
    stance: z.enum(['peaceful', 'cautious', 'aggressive', 'shadow']).optional(),
    minDurationMin: z.number().optional(),
    whenStrikingFirst: z.boolean().optional(),
    oncePerFight: z.boolean().optional(),
    stat: z.enum(['STR', 'AGI', 'INT', 'VIT', 'PER']).optional(),
    n: z.number().int().min(2).optional(),
    maxStacks: z.number().int().min(1).optional(),
    scope: z.enum(['fight', 'expedition']).optional(),
    rounds: z.number().int().min(1).optional(),
    pctOfAttack: z.number().optional(),
    reflectPct: z.number().optional(),
    durability: z.number().optional(),
    maxPerFight: z.number().optional(),
    item: z.string().optional(),
    minRarity: z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary']).optional(),
    maxRarity: z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary']).optional(),
    excludes: z.array(z.string()).optional(),
    when: z.string().optional(),
    bandage: z.number().int().optional(),
    medkit: z.number().int().optional(),
    /** resourceFindPct: which resource (krug 5, F10). */
    resource: z.string().optional(),
    /** Only in zones with open PvP (Military Base, Wasteland, Survivor Vault). */
    openPvpOnly: z.boolean().optional(),
  })
  .strict();

const zLevel = z.object({ desc: z.string().min(3), effects: z.array(zEffect).min(1) }).strict();
const zItem = z
  .object({
    id: z.string(),
    signature: z.object({ name: z.string().min(2), rare: zLevel, epic: zLevel }).strict(),
    legendary: z.object({ name: z.string().min(2), desc: z.string().min(3), effects: z.array(zEffect).min(1) }).strict(),
    note: z.string().optional(),
  })
  .strict();
const zFile = z.object({ version: z.number(), source: z.string(), rules: z.record(z.string(), z.string()), effectTypes: z.array(z.string()), items: z.array(zItem) }).strict();

export type PerkEffect = z.infer<typeof zEffect> & { type: PerkEffectType };
export type ItemPerkDef = z.infer<typeof zItem>;

/**
 * Checks the perk file: every MVP item exactly once with rare, epic and legendary, and the
 * listed effect types are exactly the 97 the code knows. Throws with every problem at once.
 */
export function validatePerkFile(data: unknown): ItemPerkDef[] {
  const parsed = zFile.safeParse(data);
  if (!parsed.success) throw new Error(`item_perks_mvp.json: ${parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`);
  const f = parsed.data;
  const errors: string[] = [];
  const known = new Set<string>(PERK_EFFECT_TYPES);
  const listed = new Set(f.effectTypes);
  for (const t of listed) if (!known.has(t)) errors.push(`unknown effect type "${t}"`);
  for (const t of known) if (!listed.has(t)) errors.push(`effect type "${t}" missing from effectTypes`);
  const ids = f.items.map((i) => i.id);
  const mvp = new Set<string>(MVP_ITEMS.map((i) => i.id));
  for (const id of ids) if (!mvp.has(id)) errors.push(`"${id}" is not an MVP item`);
  for (const id of mvp) if (!ids.includes(id)) errors.push(`"${id}" has no perks`);
  if (new Set(ids).size !== ids.length) errors.push('an item is listed twice');
  if (errors.length) throw new Error(`item_perks_mvp.json: ${errors.join('; ')}`);
  return f.items;
}

export const ITEM_PERK_DEFS: Record<string, ItemPerkDef> = Object.fromEntries(validatePerkFile(raw).map((d) => [d.id, d]));
export const PERK_RULES: Record<string, string> = (raw as { rules: Record<string, string> }).rules;

/** Marker in `ItemInstance.passive` for items that carry their catalog perks (krug 4). */
export const SIGNATURE_PASSIVE_ID = 'signature';

export interface PerkView {
  name: string;
  desc: string;
  effects: PerkEffect[];
}

export interface ItemPerks {
  /** Signature perk at this rarity (Rare: level 1, Epic and Legendary: level 2). */
  signature: (PerkView & { level: 'rare' | 'epic' }) | null;
  /** On a Rare: what the same perk does on Epic (the grey line in the item detail). */
  next: PerkView | null;
  legendary: PerkView | null;
}

const NONE: ItemPerks = { signature: null, next: null, legendary: null };

/** The catalog perks of a template at a rarity (whether or not a given item carries them). */
export function perksFor(templateId: string, rarity: Rarity): ItemPerks {
  const d = ITEM_PERK_DEFS[templateId];
  if (!d || rarity === 'common' || rarity === 'uncommon') return NONE;
  const sig = d.signature;
  const lvl = rarity === 'rare' ? 'rare' : 'epic';
  return {
    signature: { name: sig.name, desc: sig[lvl].desc, effects: sig[lvl].effects as PerkEffect[], level: lvl },
    next: rarity === 'rare' ? { name: sig.name, desc: sig.epic.desc, effects: sig.epic.effects as PerkEffect[] } : null,
    legendary: rarity === 'legendary' ? { name: d.legendary.name, desc: d.legendary.desc, effects: d.legendary.effects as PerkEffect[] } : null,
  };
}

/** Whether an item gets a signature marker when it is made (Rare and up with catalog perks). */
export function rollsSignature(templateId: string, rarity: Rarity): boolean {
  return !!perksFor(templateId, rarity).signature;
}

/** Whether this saved item carries catalog perks (made in krug 4 or later). */
export function hasSignature(item: Pick<ItemInstance, 'passive'>): boolean {
  return item.passive?.id === SIGNATURE_PASSIVE_ID;
}

/** The perks this item really has: catalog perks for signature items, none otherwise. */
export function itemPerks(item: Pick<ItemInstance, 'templateId' | 'rarity' | 'passive'>): ItemPerks {
  return hasSignature(item) ? perksFor(item.templateId, item.rarity) : NONE;
}

/** Effect with the item it came from (self-wear effects need it). */
export type ActivePerkEffect = PerkEffect & { src: string };

/** All effects of an item's perks (signature and Legendary). */
export function itemPerkEffects(item: Pick<ItemInstance, 'id' | 'templateId' | 'rarity' | 'passive'>): ActivePerkEffect[] {
  const p = itemPerks(item);
  return [...(p.signature?.effects ?? []), ...(p.legendary?.effects ?? [])].map((e) => ({ ...e, src: item.id }));
}

// ---- Reading effects ---------------------------------------------------------------------

export interface PerkConditions {
  zone?: ZoneId | string;
  stance?: Stance;
  /** HP fraction of the perk's owner (0–1). */
  hpPct?: number;
  /** Expedition length in minutes. */
  minutes?: number;
  /** The attack type of the owner's own weapon, or of the attack that meets the effect. */
  attack?: 'melee' | 'ranged';
  /** Targets the other side matches. */
  targets?: PerkTarget[];
  strikingFirst?: boolean;
}

/** Does an effect apply under these conditions? Unknown conditions do not match. */
export function perkApplies(e: PerkEffect, c: PerkConditions): boolean {
  if (e.zones && !(c.zone && e.zones.includes(c.zone))) return false;
  if (e.openPvpOnly && !(c.zone && ZONES[c.zone as ZoneId]?.pvp === 'open')) return false;
  if (e.stance && e.stance !== c.stance) return false;
  if (e.belowHpPct != null && !(c.hpPct != null && c.hpPct * 100 < e.belowHpPct)) return false;
  if (e.minDurationMin != null && !(c.minutes != null && c.minutes >= e.minDurationMin)) return false;
  if (e.attack && e.attack !== c.attack) return false;
  if (e.targets && !(c.targets && e.targets.some((t) => c.targets!.includes(t)))) return false;
  if (e.whenStrikingFirst && !c.strikingFirst) return false;
  return true;
}

/** Whether an effect has any condition at all (unconditional ones fold into the stats). */
export function isConditional(e: PerkEffect): boolean {
  return !!(e.zones || e.openPvpOnly || e.stance || e.belowHpPct != null || e.minDurationMin != null || e.attack || e.targets || e.whenStrikingFirst);
}

/** Sum of the numeric values of one effect type that apply (same type from several items adds up). */
export function perkSum(fx: readonly PerkEffect[] | undefined, type: PerkEffectType, c: PerkConditions = {}): number {
  let s = 0;
  for (const e of fx ?? []) if (e.type === type && typeof e.value === 'number' && perkApplies(e, c)) s += e.value;
  return s;
}

/** Whether a flag effect (value true) of this type applies. */
export function perkFlag(fx: readonly PerkEffect[] | undefined, type: PerkEffectType, c: PerkConditions = {}): boolean {
  return (fx ?? []).some((e) => e.type === type && e.value !== false && perkApplies(e, c));
}

/** The effects of one type that apply (for effects with extra fields: n, rounds, item…). */
export function perkEffectsOf(fx: readonly PerkEffect[] | undefined, type: PerkEffectType, c: PerkConditions = {}): PerkEffect[] {
  return (fx ?? []).filter((e) => e.type === type && perkApplies(e, c));
}
